import React, {useCallback, useState} from 'react'
import {SelectPureWithInputContainer, useRenderPersistedArray} from '@modifi/plugin-onboarding'
import {useTranslation} from '@modifi/plugin-i18n'
import {Row, Col, CustomerDialogButtonRow, H5} from '@modifi/ui'
import {
  CompanyDocumentUpload,
  DocumentTypeLabelForStupidInconsistency,
} from '../../../../plugins/onboarding'
import DocumentTypeRowWithDescription from '../../components/company-data/document-type-row-with-description'

type DocType = {
  fileId: string
  documentType: string
}

const uploadFormComponents = {
  documentTypeLabel: DocumentTypeLabelForStupidInconsistency,
  documentTypeRow: DocumentTypeRowWithDescription,
}

type TransportDocumentUploadDialogProps = {
  params: {
    onFileUploaded: (document: {fileId: string; documentType: string}) => void
    onFileChange: (document: {fileId: string; documentType: string}) => void
    onDeleteFile: (fileId: string) => void
    isDisabled: (documentType: string) => boolean
    availableDocumentTypes: string[]
    previouslyUploadedFiles: {
      fileId: string
      documentType: string
    }[]
    isReadOnly: boolean
  }
  closeDialog: () => void
}

const TransportDocumentUploadDialog: React.FC<TransportDocumentUploadDialogProps> = ({
  params,
  closeDialog,
}) => {
  const {
    onFileUploaded,
    onFileChange,
    onDeleteFile,
    isDisabled,
    availableDocumentTypes,
    previouslyUploadedFiles,
    isReadOnly,
  } = params
  const {t} = useTranslation('charon')
  const {t: tShared} = useTranslation('shared')

  const [transportDocumentType, setTransportDocumentType] = useState(
    previouslyUploadedFiles?.[0]?.documentType
  )
  const [uploadedFiles, setUploadedFiles] = useState(previouslyUploadedFiles)

  const handleTransportDocumentChange = useCallback(({value}: {value: string}) => {
    setTransportDocumentType(value)
  }, [])

  const handleFileUploaded = useCallback(
    (document: DocType) => {
      onFileUploaded(document)
      setUploadedFiles(data => [...data, document])
    },
    [onFileUploaded]
  )

  const handleDeleteFile = useCallback(
    (fileId: string) => {
      onDeleteFile(fileId)
      setUploadedFiles(files => files.filter(f => f?.fileId !== fileId))
    },
    [onDeleteFile]
  )

  const transportDocumentOption = useRenderPersistedArray(
    availableDocumentTypes.map(value => ({
      value,
      label: tShared(`documentType.${value}`),
    }))
  )

  return (
    <>
      <Row>
        <Col>
          <H5 textAlign="center">{t('trade-partner.transport-document-upload.headline')}</H5>
        </Col>
      </Row>
      <Row>
        <Col col={8} offset={2}>
          <SelectPureWithInputContainer
            name="transportDocumentType"
            label={t('trade-partner.transport-document-type.label')}
            placeholder={t('trade-partner.transport-document-type.placeholder.label')}
            options={transportDocumentOption}
            value={transportDocumentType}
            onChange={handleTransportDocumentChange}
            disabled={uploadedFiles.length > 0 || isReadOnly}
          />
        </Col>
        {transportDocumentType && (
          <Col col={8} offset={2}>
            <CompanyDocumentUpload
              withoutOther
              withoutCta
              withInstantFileUpload
              multiple
              t={t}
              previouslyUploadedFiles={uploadedFiles}
              availableDocumentTypes={[transportDocumentType]}
              onFileUploaded={handleFileUploaded}
              onFileChange={onFileChange}
              onDeleteFile={handleDeleteFile}
              isDisabled={isDisabled}
              components={uploadFormComponents}
              canDeleteDocument={!isReadOnly}
            />
          </Col>
        )}
      </Row>
      <CustomerDialogButtonRow
        canSubmit
        onSubmitClick={closeDialog}
        submitText={t('trade-partner.transport-document-type.cta')}
      />
    </>
  )
}

export default TransportDocumentUploadDialog
